import {
  normalizeSubscriptionRequest,
  ProviderSubscriptionStatus,
  subscriptionKey
} from "./market-data-contracts.js";

export class SubscriptionRegistry {
  constructor(onDesiredChange = () => {}) {
    this.onDesiredChange = onDesiredChange;
    this.entries = new Map();
    this.consumerSequence = 0;
  }

  acquire(request, listener = () => {}) {
    const normalized = normalizeSubscriptionRequest(request);
    const key = subscriptionKey(normalized);
    let entry = this.entries.get(key);
    const wasDesired = Boolean(entry?.consumers.size);
    if (!entry) {
      entry = {
        key,
        request: normalized,
        consumers: new Map(),
        providerStatus: ProviderSubscriptionStatus.PENDING,
        providerMetadata: null
      };
      this.entries.set(key, entry);
    }
    const consumerId = ++this.consumerSequence;
    entry.consumers.set(consumerId, listener);
    if (!wasDesired) this.onDesiredChange(entry, true);
    if (wasDesired) {
      try {
        listener(Object.freeze({
          kind: "subscription-status",
          key,
          status: entry.providerStatus,
          metadata: entry.providerMetadata
        }));
      } catch {
        // A newly attached consumer cannot disrupt an existing subscription.
      }
    }
    let released = false;
    return Object.freeze({
      key,
      release: () => {
        if (released) return false;
        released = true;
        entry.consumers.delete(consumerId);
        if (entry.consumers.size === 0) this.onDesiredChange(entry, false);
        return true;
      }
    });
  }

  desiredEntries() {
    return [...this.entries.values()].filter(entry => entry.consumers.size > 0);
  }

  get(key) {
    return this.entries.get(key) || null;
  }

  pruneUnused(activeKeys = new Set()) {
    for (const [key, entry] of this.entries) {
      if (entry.consumers.size === 0 && !activeKeys.has(key)) this.entries.delete(key);
    }
  }

  markResult(key, status, metadata = null) {
    const entry = this.entries.get(key);
    if (!entry) return;
    entry.providerStatus = status;
    entry.providerMetadata = metadata;
    this.notify(entry, Object.freeze({
      kind: "subscription-status",
      key,
      status,
      metadata
    }));
  }

  dispatch(message) {
    for (const entry of this.entries.values()) {
      if (entry.consumers.size === 0 ||
          entry.request.providerSymbol !== message.providerSymbol ||
          entry.request.type !== message.type) continue;
      const normalized = Object.freeze({
        ...message,
        internalSymbolId: entry.request.internalSymbolId
      });
      this.notify(entry, normalized);
    }
  }

  clear() {
    this.entries.clear();
  }

  snapshot() {
    return this.desiredEntries().map(entry => Object.freeze({
      key: entry.key,
      request: entry.request,
      referenceCount: entry.consumers.size,
      providerStatus: entry.providerStatus,
      providerMetadata: entry.providerMetadata
    }));
  }

  notify(entry, value) {
    for (const listener of entry.consumers.values()) {
      try {
        listener(value);
      } catch {
        // A consumer cannot take down the shared market-data stream.
      }
    }
  }
}
